/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { action } from '@ember/object';
import { isEmpty, isPresent } from '@ember/utils';
import ExtApiKeyModel from 'cio/models/ext-api-key';
import ApiCredentialModel from 'cio/models/api-credential';
import WhitelistedIpModel from 'cio/models/whitelisted-ip';
import AlertsService from 'cio/services/alerts';
import apiKeyTypeInterface, { ApiCredentialType } from 'cio/utils/api-key-type-interface';
import AnalyticsService from 'cio/services/analytics';
import EnvironmentModel from 'cio/models/environment';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import SigningSecretModel from 'cio/models/signing-secret';

interface ManageApiKeysComponentArgs {
  appKeys: ExtApiKeyModel[];
  trackingKeys: ApiCredentialModel[];
  signingKey: SigningSecretModel[];
  showWebhookSigningKey: boolean;
  whitelistedIpAddresses: WhitelistedIpModel[];
  environments: EnvironmentModel[];
  environmentId?: string;
  keyType: string;
  routeName: string;
}

export default class ManageApiKeysComponent extends Component<ManageApiKeysComponentArgs> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked lastCreatedAppApiKey!: ExtApiKeyModel | null;
  @tracked keyPendingDeletion!: ExtApiKeyModel | ApiCredentialModel | null;
  @tracked newCredentials: ApiCredentialModel[] = [];

  ApiCredentialType = ApiCredentialType;

  get appKeys() {
    if (!isEmpty(this.args.environmentId) && isPresent(this.args.appKeys)) {
      return this.args.appKeys.filter(
        (k) => (k.get('isNew') && !k.get('isDeleted')) || k.environment.id === this.args.environmentId,
      );
    }

    return this.args.appKeys;
  }

  get trackingKeys() {
    return [...this.newCredentials, ...this.args.trackingKeys].filter((c) => !c.get('isDeleted'));
  }

  get signingKey() {
    return this.args.signingKey;
  }

  get uiText() {
    return apiKeyTypeInterface[this.args.keyType].uiText;
  }

  get recordType() {
    return apiKeyTypeInterface[this.args.keyType].modelName;
  }

  createWhitelistedIp = task(async (whitelistedIp: WhitelistedIpModel) => {
    await whitelistedIp.save();
    this.alerts.success(`whitelistedIp.create.success`, `${whitelistedIp.ip} whitelisted`);
  });

  destroyWhitelistedIp = task(async (whitelistedIp: WhitelistedIpModel) => {
    await whitelistedIp.destroyRecord();
    this.alerts.success(`whitelistedIp.delete.success`, `${whitelistedIp.ip} removed`);
  });

  saveNewAppApiKey = task(async (key: ExtApiKeyModel) => {
    await key.save();
    this.lastCreatedAppApiKey = key;
  });

  updateCredential = task(async (credential: ExtApiKeyModel & ApiCredentialModel) => {
    let isNew = credential.get('isNew');
    await credential.save();
    this.alerts.success(
      `${this.recordType}.update.success`,
      `${credential.name} credentials ${isNew ? 'created' : 'updated'}`,
    );
  });

  destroyCredential = task(async (credential: ExtApiKeyModel | ApiCredentialModel) => {
    await credential.destroyRecord();

    this.keyPendingDeletion = null;
    this.alerts.success(`${this.recordType}.delete.success`, `${credential.name} credentials deleted`);
  });

  @action
  trackKeysViewed(keyType: string) {
    if (keyType === 'app') {
      this.analytics.track('integration', 'app_api_keys_viewed');
    } else if (keyType === 'tracking') {
      this.analytics.track('integration', 'tracking_api_keys_viewed');
    }
  }

  @action
  createNewCredential() {
    this.analytics.track('integration', 'api_credentials_creation_started', {
      keyType: `${this.args.keyType.charAt(0).toUpperCase()}${this.args.keyType.slice(1)}`,
    });

    let newRecord = this.store.createRecord(this.recordType);
    if (this.recordType === 'api-credential') {
      this.newCredentials = [newRecord, ...this.newCredentials];
    }
  }

  @action
  createNewAddress() {
    this.store.createRecord('whitelisted-ip');
  }

  @action
  trackKeyCopy() {
    this.analytics.track('integration', 'api_credentials_copied', {
      credentialType: 'App',
      credentialCopied: 'Api Key',
    });
  }
}
